/// <amd-module name="Repos/Discussion/Editor/Widgets/DiscussionBaseWidget" />
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import {
    IDiffDiscussionWidget,
    IDiscussionThreadModel,
    ILineInfo,
    LineInfoType,
    SpecialCommentPositions
} from "Repos/Discussion/Common/DiscussionContracts";
import { IReadonlyObservableArray, ObservableArray } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";

export interface IDiscussionWidgetContentProps {
    className?: string;
    onContentLoaded?: (contentElement: HTMLElement | null) => void;
    threads: IReadonlyObservableArray<IDiscussionThreadModel>;
}

export interface IDiscussionBaseWidgetProps<TContentProps extends IDiscussionWidgetContentProps> {
    addToOriginalDiff: boolean;
    contentClassName?: string;
    hideOnOrientations?: DiffViewerOrientation[];
    lineInfo?: ILineInfo;
    pageContext: IVssPageContext;
    renderContent: (props: TContentProps) => JSX.Element;
    threads?: IDiscussionThreadModel[];
}

export class DiscussionBaseWidget<TProps extends IDiscussionBaseWidgetProps<IDiscussionWidgetContentProps>> implements IDiffDiscussionWidget {
    protected props: TProps;
    protected threads: ObservableArray<IDiscussionThreadModel>;

    constructor(props: TProps) {
        this.props = props;
        this.threads = new ObservableArray<IDiscussionThreadModel>(props.threads || []);
    }

    public addThread(thread: IDiscussionThreadModel): void {
        this.threads.push(thread);
    }

    public belongsToLine(lineNumber: number, addToOriginalDiff: boolean, infoType: LineInfoType): boolean {
        return !!this.props.lineInfo && this.props.lineInfo.lineEnd === lineNumber && this.props.addToOriginalDiff === addToOriginalDiff;
    }

    public getLineNumber(addToOriginalDiff: boolean): number {
        if (!!this.props.lineInfo && this.props.addToOriginalDiff === addToOriginalDiff) {
            return this.props.lineInfo.lineEnd;
        }

        return -1;
    }

    public getThreadCount(): number {
        return this.threads.length;
    }

    public containsThread(threadId: number): boolean {
        return this.threads.value.some(t => t.id === threadId);
    }

    public hasThreads(): boolean {
        return this.threads.length > 0;
    }

    public hideOnOrientation(orientation: DiffViewerOrientation): boolean {
        return this.props.hideOnOrientations ? this.props.hideOnOrientations.some(x => x == orientation) : false;
    }

    public isAttachedToFile(): boolean {
        const { lineInfo } = this.props;
        return !lineInfo || lineInfo.lineEnd === SpecialCommentPositions.File;
    }

    public removeThread(threadId: number): void {
        const threadIndex = this.threads.value.findIndex(t => t.id === threadId);
        if (threadIndex >= 0) {
            this.threads.splice(threadIndex, 1);
        }
    }

    public render(props?: { className?: string }): JSX.Element {
        return this.props.renderContent({ ...props, threads: this.threads });
    }
}
